/**
 * @file AddItemModal.tsx
 * @description Modal component for adding new work items to the Focus Hub
 * @module Modals/AddItemModal
 */

import React, { useState } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, Tag, FileText, Link2, Folder, Hash } from 'lucide-react';
import { WorkItem, WorkItemStatus } from '../../types';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../services/database';
import { validators, validateForm, ValidationSchema } from '../../utils/errorHandling';

/**
 * Props for the AddItemModal component
 * @interface AddItemModalProps
 */
export interface AddItemModalProps {
  parentCategory?: { level1: string; level2: string };
  onSave: (item: WorkItem) => void;
  onClose: () => void;
  customCategories?: string[];
  onOpenCategoryManagement?: () => void;
}

export const AddItemModal: React.FC<AddItemModalProps> = ({ parentCategory, onSave, onClose, customCategories, onOpenCategoryManagement }) => {
  // Special value for the category management option
  const CATEGORY_MANAGEMENT_OPTION = '__MANAGE_CATEGORIES__';
  const [categories, setCategories] = useState<string[]>([]);
  const [subcategories, setSubcategories] = useState<Record<string, string[]>>({});
  const [savedTags, setSavedTags] = useState<string[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  
  const [formData, setFormData] = useState({
    level1: '',
    level2: '',
    fileName: '',
    link: '',
    description: '',
    tags: [] as string[],
    status: undefined as WorkItemStatus | undefined,
  });
  const [tagInput, setTagInput] = useState('');

  // Load categories, subcategories, and saved tags
  React.useEffect(() => {
    // Load categories
    const savedCategories = localStorage.getItem('cleanRoomCategories');
    const categoryList = savedCategories ? JSON.parse(savedCategories) : ['What I am working on', 'What I am learning', 'What I am interested'];
    setCategories(categoryList);
    
    // Load subcategories
    const savedSubcategories = localStorage.getItem('cleanRoomSubcategories');
    const subcategoryMap = savedSubcategories ? JSON.parse(savedSubcategories) : {};
    setSubcategories(subcategoryMap);
    
    // Load saved tags and collect tags from existing items
    const collectExistingTags = async () => {
      try {
        const allTags = new Set<string>();
        
        // Get tags from existing items in the IndexedDB database
        const items = await db.getAllWorkItems();
        console.log('[AddItemModal] Total items in database:', items.length);
        
        // Collect all unique tags from existing items
        items.forEach((item: WorkItem) => {
          if (item.tags && Array.isArray(item.tags)) {
            item.tags.forEach((tag: string) => {
              if (tag && tag.trim()) {
                console.log(`[AddItemModal] Found tag "${tag}" in item "${item.fileName}"`);
                allTags.add(tag.trim());
              }
            });
          }
        });
        
        const uniqueTags = Array.from(allTags).sort();
        console.log('[AddItemModal] All unique tags collected:', uniqueTags);
        setSavedTags(uniqueTags);
        
        // Clear old localStorage tags that might have hardcoded values
        localStorage.removeItem('focusHubSavedTags');
      } catch (e) {
        console.error('Failed to load tags:', e);
      }
    };
    
    collectExistingTags();
    
    // Set default category
    if (parentCategory?.level1) {
      setFormData(prev => ({
        ...prev,
        level1: parentCategory.level1,
        level2: parentCategory.level2 || ''
      }));
    } else if (categoryList.length > 0) {
      setFormData(prev => ({
        ...prev,
        level1: categoryList[0]
      }));
    }
  }, [parentCategory]);

  // Handle ESC key press
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Check if there are suggestions to show
  const filteredSuggestions = savedTags.filter(tag =>
    !formData.tags.includes(tag) &&
    (tagInput === '' || tag.toLowerCase().includes(tagInput.toLowerCase()))
  );
  const hasVisibleSuggestions = showTagSuggestions && filteredSuggestions.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationSchema: ValidationSchema = {
      level1: [{ validator: (v) => validators.required(v, 'Category') }],
      fileName: [
        { validator: (v) => validators.required(v, 'File name') },
        { validator: (v) => validators.minLength(v, 1, 'File name') },
        { validator: (v) => validators.maxLength(v, 255, 'File name') },
        { validator: validators.validFileName }
      ],
      link: [{ validator: validators.url }]
    };
    
    const errors = validateForm(formData, validationSchema);
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      alert(firstError); // TODO: Better error display
      return;
    }

    const newItem: WorkItem = {
      id: uuidv4(),
      level1: formData.level1,
      level2: formData.level2 || 'Items', // Default to 'Items' if no level2
      fileName: formData.fileName,
      link: formData.link,
      description: formData.description,
      comments: [],
      tags: formData.tags,
      status: formData.status,
      metadata: {
        created: new Date(),
        modified: new Date(),
        author: localStorage.getItem('userName') || 'User',
        version: 1}};

    // Save new tags to localStorage
    if (formData.tags.length > 0) {
      const currentSavedTags = localStorage.getItem('focusHubSavedTags');
      let allTags = currentSavedTags ? JSON.parse(currentSavedTags) : [];
      
      // Add new tags that aren't already saved
      formData.tags.forEach(tag => {
        if (!allTags.includes(tag)) {
          allTags.push(tag);
        }
      });
      
      // Keep only the most recent 50 tags
      if (allTags.length > 50) {
        allTags = allTags.slice(-50);
      }
      
      localStorage.setItem('focusHubSavedTags', JSON.stringify(allTags));
    }

    onSave(newItem);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]}));
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)}));
  };

  return (
    <div style={{...styles.modal.overlay}} onClick={onClose}>
      <div style={{...styles.modal.container}} onClick={(e) => e.stopPropagation()}>
        <div style={{...styles.modal.header}}>
          <h2 style={{...styles.modal.title}}>
            Add New Item
          </h2>
          <button onClick={onClose} style={{...styles.modal.close}}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{...styles.modal.body}}>
            <div style={{...styles.form.group}}>
              <label style={{...styles.form.label}}>
                <Folder size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Category
              </label>
              <select
                value={formData.level1}
                onChange={e => {
                  const value = e.target.value;
                  if (value === CATEGORY_MANAGEMENT_OPTION) {
                    // Close this modal and open category management
                    if (onOpenCategoryManagement) {
                      onClose();
                      onOpenCategoryManagement();
                    }
                  } else {
                    setFormData(prev => ({ ...prev, level1: value, level2: '' }));
                  }
                }}
                style={{...styles.form.select}}
                disabled={!!parentCategory}
                required
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
                {onOpenCategoryManagement && (
                  <>
                    <option disabled>──────────</option>
                    <option
                      value={CATEGORY_MANAGEMENT_OPTION}
                      style={{
                        fontWeight: '600',
                        color: '#3b82f6',
                        background: '#eff6ff'
                      }}
                    >
                      ➕ Add/Edit category
                    </option>
                  </>
                )}
              </select>
              {categories.length === 0 && (
                <p style={{...styles.form.error}}>No categories available. Please create categories first.</p>
              )}
            </div>

            {subcategories[formData.level1] && subcategories[formData.level1].length > 0 && (
              <div style={{...styles.form.group}}>
                <label style={{...styles.form.label}}>
                  Subcategory <span style={{ opacity: 0.6 }}>(Optional)</span>
                </label>
                <select
                  value={formData.level2}
                  onChange={e => setFormData(prev => ({ ...prev, level2: e.target.value }))}
                  style={{...styles.form.select}}
                  disabled={!!parentCategory?.level2}
                >
                  <option value="">None (Direct item)</option>
                  {subcategories[formData.level1].map(subcategory => (
                    <option key={subcategory} value={subcategory}>{subcategory}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{...styles.form.group}}>
              <label style={{...styles.form.label}}>
                <FileText size={14} style={{ display: 'inline', marginRight: '4px' }} />
                File Name *
              </label>
              <input
                type="text"
                value={formData.fileName}
                onChange={e => setFormData(prev => ({ ...prev, fileName: e.target.value }))}
                style={{...styles.form.input}}
                placeholder="Enter file or item name"
                required
              />
            </div>

            <div style={{...styles.form.group}}>
              <label style={{...styles.form.label}}>
                <Link2 size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Link
              </label>
              <input
                type="url"
                value={formData.link}
                onChange={e => setFormData(prev => ({ ...prev, link: e.target.value }))}
                style={{...styles.form.input}}
                placeholder="https://..."
              />
            </div>

            <div style={{...styles.form.group}}>
              <label style={{...styles.form.label}}>
                <Hash size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Notes
              </label>
              <textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                style={{...styles.form.textarea}}
                rows={3}
                placeholder="Add any notes or description..."
              />
            </div>

            <div style={hasVisibleSuggestions ? {...styles.form.groupWithDropdown} : {...styles.form.group}}>
              <label style={{...styles.form.label}}>
                <Tag size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Tags
              </label>
              <div style={{...styles.form.tagContainer}}>
                {formData.tags.map((tag, index) => (
                  <span key={index} style={{...styles.form.tagPill}}>
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(index)}
                      style={{...styles.form.tagRemove}}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={e => {
                    setTagInput(e.target.value);
                    setShowTagSuggestions(true);
                  }}
                  onFocus={() => setShowTagSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  style={{...styles.form.tagInput}}
                  placeholder={formData.tags.length === 0 ? "Add tags..." : ""}
                />
                {hasVisibleSuggestions && (
                  <div style={styles.form.tagSuggestionsDropdown}>
                  {filteredSuggestions
                    .slice(0, 10)
                    .map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            tags: [...prev.tags, tag]}));
                          setTagInput('');
                          setShowTagSuggestions(false);
                        }}
                        style={styles.form.tagSuggestionItem}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{...styles.form.group}}>
              <label style={{...styles.form.label}}>
                Status <span style={{ opacity: 0.6 }}>(Optional)</span>
              </label>
              <select
                value={formData.status || ''}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  status: e.target.value ? e.target.value as WorkItemStatus : undefined
                }))}
                style={{...styles.form.select}}
              >
                <option value="">No Status</option>
                <option value="Backlog">Backlog</option>
                <option value="In Progress">In Progress</option>
                <option value="Done">Done</option>
                <option value="Blocked">Blocked</option>
              </select>
              <p style={{...styles.form.helper}}>Choose a status or leave empty to not track</p>
            </div>
          </div>

          <div style={{...styles.modal.footer}}>
          <button
            type="button"
            onClick={onClose}
            style={{...styles.button.cancel}}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(229, 231, 235, 0.9)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(243, 244, 246, 0.8)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{...styles.button.gradient}}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.25)';
            }}
          >
            Add Item
          </button>
          </div>
        </form>
      </div>
    </div>
  );
};